import React, { useCallback } from "react";
import { orderBy } from "lodash";
import { useNavigate, useParams } from "react-router";
import { OrganizationType } from "@/features/organization/graphql/generated";
import { IconName } from "@/shared/ui/Icon";
import {
  EntityOption,
  EntitySelectDropdown,
} from "@/shared/widgets/EntitySelectDropdown";
import { useModal } from "@/shared/utils/hooks/useModal";
import { ModalFileName } from "@/shared/modals";
import { useOrganisations } from "@/features/organization/hooks/useOrganisations";
import { useOrganisation } from "@/features/organization/hooks/useOrganisation";
import { useAccount } from "@/features/workspace/hooks/useAccount";
import {
  DropdownMenuContentProps,
  DropdownMenuProps,
} from "@/shared/ui/DropDown";
import useViewerPermissions from "@/features/workspace/hooks/useViewerPermissions";
import { AccessLevel, RoleType } from "@/shared/constants";
import { useViewerAccounts } from "@/features/workspace/hooks/useViewerAccounts";
import { useTranslation } from "react-i18next";
import { ResourceEnum } from "@/shared/constants/resources";

interface WorkspaceSelectDropdownProps extends DropdownMenuProps {
  children: React.ReactNode;
  side?: DropdownMenuContentProps["side"];
  align?: DropdownMenuContentProps["align"];
  sideOffset?: DropdownMenuContentProps["sideOffset"];
  alignOffset?: DropdownMenuContentProps["sideOffset"];
}

function OrganizationSelectDropdown(props: WorkspaceSelectDropdownProps) {
  const { children, ...rest } = props;
  const { t } = useTranslation();

  const { organization: selectedOrg, handleOrganisationSelect } =
    useOrganisation();
  const { account } = useAccount();
  const { accounts } = useViewerAccounts();
  const { validateAccessToResource } = useViewerPermissions();
  const navigate = useNavigate();
  const { organizations } = useOrganisations();

  const [openOrganizationCreateModal] = useModal(
    ModalFileName.NEW_ORGANIZATION
  );

  const hasAccessToOrgSettings = validateAccessToResource({
    resourceType: ResourceEnum.organization,
    accessLevel: AccessLevel.edit,
  });

  const hasAccessToAccountSettings = validateAccessToResource({
    resourceType: ResourceEnum.account,
    accessLevel: AccessLevel.edit,
  });

  const handleSelectOrg = useCallback(
    async (organisation: OrganizationType) => {
      // Do not need to check permissions here, as we show only organizations the user has access to
      handleOrganisationSelect(organisation);
    },
    []
  );

  const dropdownLinks = [
    {
      id: "workspace-settings",
      label: t("organisation.manage_workspace"),
      icon: "Building" as IconName,
      onClick: () => {
        navigate(`/account/${account?.id}/settings`);
      },
      disabled: !hasAccessToAccountSettings,
    },
    {
      id: "workspace-browse",
      label: t("organisation.change_workspace"),
      icon: "Kebab" as IconName,
      onClick: () => {
        navigate("/org");
      },
      separatorAfter: true,
      disabled: accounts?.length < 2,
    },
    {
      id: "organization-settings",
      label: t("organisation.current_organisation_settings"),
      icon: "Gear" as IconName,
      onClick: () => {
        navigate(`/org/${selectedOrg.id}/settings`);
      },
      disabled: !hasAccessToOrgSettings,
    },
    {
      id: "organization-create",
      label: t("organisation.create_new_organisation"),
      icon: "Plus" as IconName,
      variant: "primary" as const,
      onClick: openOrganizationCreateModal,
      disabled: !hasAccessToAccountSettings,
    },
  ].filter((link) => !link.disabled);

  return (
    <EntitySelectDropdown
      options={orderBy(organizations, "name") as EntityOption[]}
      title={t("organisation.change_organisation")}
      onSelect={(organisation: EntityOption) =>
        handleSelectOrg(organisation as OrganizationType)
      }
      selectedOption={selectedOrg.id}
      links={dropdownLinks}
      searchEnabled={organizations?.length > 5}
      contentContainerClassName={
        organizations?.length > 5 ? "min-h-[500px]" : ""
      }
      {...rest}
    >
      {children}
    </EntitySelectDropdown>
  );
}

export default OrganizationSelectDropdown;
export { OrganizationSelectDropdown };
